import React, { forwardRef, useCallback, useMemo } from 'react';
import BaseAccordion, { AccordionProps } from '@mui/material/Accordion';

import useControlledState from '../hooks/useControlledState';
import AccordionContext from './AccordionContext';

type Props = Omit<AccordionProps, 'onChange'> & {
  onChange?: (expanded: boolean) => void;
};

const Accordion = forwardRef<HTMLDivElement, Props>((props, ref) => {
  const { defaultExpanded, expanded: expandedProp, onChange: onChangeProp, ...rest } = props;

  const [expanded, setExpanded] = useControlledState({
    controlledValue: expandedProp,
    defaultValue: defaultExpanded,
    onChange: onChangeProp,
  });

  const contextValue = useMemo(
    () => ({ expanded, toggle: () => setExpanded(!expanded) }),
    [expanded, setExpanded],
  );

  const onChange = useCallback(
    (_: React.SyntheticEvent<Element, Event>, nextExpanded: boolean) => {
      setExpanded(nextExpanded);
    },
    [setExpanded],
  );

  return (
    <AccordionContext.Provider value={contextValue}>
      <BaseAccordion ref={ref} {...rest} expanded={expanded} onChange={onChange} />
    </AccordionContext.Provider>
  );
});

export default Accordion;
